import type { Alphabet } from './alphabet';
import type { CellIndex, Dir } from './grid';
import type { ContentPack, Question } from './pack';
import type { EngineRules } from './rules';

export type Phase = 'play' | 'revive' | 'over';
/**
 * clear = 8문제 전부 해결(봉인 성공) · unsealed = 8문제 소진했으나 미해결 ·
 * engorged = 논리 길이가 상한에 닿음(판 종료, 사망이 아니다) · wall/self = 사망(설계원칙 1의 둘뿐)
 */
export type EndReason =
  | 'running'
  | 'wall'
  | 'self'
  | 'clear'
  | 'unsealed'
  | 'engorged'
  | 'tick-limit'
  | 'quit'
  | 'abandoned';
export type WordOutcome = 'solved' | 'skipped' | 'died';

// ── 부활 (RULES §1.14 · ADR-032) ────────────────────────────────────────────
export type ReviveStatus = 0 | 1 | 2 | 3; // 0 idle · 1 open · 2 공개(성공) · 3 공개(실패)
export type ReviveResult = 'none' | 'correct' | 'wrong' | 'timeout';
export type DeathCause = 'wall' | 'self';

export interface ReviveState {
  readonly status: ReviveStatus;
  readonly offered: number;
  readonly used: number;
  readonly cause: DeathCause | null;
  readonly questionId: string;
  readonly choices: readonly string[];
  readonly correctIndex: number; // 판정 전에는 뷰로 나가지 않는다 (toReviveView 가 마스킹)
  readonly picked: number; // -1 = 미선택
  readonly armTicksLeft: number;
  readonly ticksLeft: number;
  readonly revealTicksLeft: number;
  readonly openTick: number;
  readonly answerTick: number;
  readonly result: ReviveResult;
}

export const EMPTY_REVIVE: ReviveState = {
  status: 0,
  offered: 0,
  used: 0,
  cause: null,
  questionId: '',
  choices: [],
  correctIndex: -1,
  picked: -1,
  armTicksLeft: 0,
  ticksLeft: 0,
  revealTicksLeft: 0,
  openTick: -1,
  answerTick: -1,
  result: 'none',
};

/** 뷰 투영 — 판정 전에는 정답 위치가 담기지 않는다(ADR-008 확장). */
export interface ReviveView {
  readonly status: ReviveStatus;
  readonly cause: DeathCause | null;
  readonly question: string;
  readonly choices: readonly string[];
  readonly picked: number;
  readonly revealIndex: number; // status >= 2 일 때만 >= 0
  readonly armed: boolean;
  readonly ticksLeft: number;
  readonly limitTicks: number; // 게이지 분모
}

export interface ChoiceEntry {
  readonly t: number;
  readonly c: number;
}
export type ChoiceLog = readonly ChoiceEntry[];
export type PenaltyStatus = 0 | 1 | 2; // 0 none · 1 deferred · 2 active

export interface Tile {
  readonly cell: CellIndex;
  readonly ch: string; // 완성형 음절 1자
  readonly kind: 0 | 1; // 0=정답 1=오답 — 뷰에 절대 전달하지 않는다
  readonly slot: number; // 정답 내 자리(0-based). kind=1 이면 -1
}

export interface SnakeState {
  readonly cells: readonly CellIndex[]; // [0] = 머리
  readonly dir: Dir;
  /**
   * 부호 있는 미소비 길이 변화. 논리 길이 = cells.length + pending.
   * 이동 틱당 절대값 1칸씩만 물리 셀에 실현된다(SEAL §5.A).
   */
  readonly pending: number;
}

/** 길이 원장 — 모든 항은 부호 있는 칸 수. 표시 순서 고정, 크기순 정렬 금지(설계원칙 1). */
export interface LenLedger {
  readonly init: number;
  readonly byTime: number; // 자동 성장 (+)
  readonly byAnswer: number; // 정답 글자 (−)
  readonly byWord: number; // 단어 완성 (−)
  readonly byWrong: number; // 오답 (+)
}

export interface WordState {
  readonly index: number;
  readonly questionId: string;
  readonly answer: string; // 엔진 내부 전용 — 뷰에 넘기지 않는다
  readonly eaten: number;
  readonly showTick: number;
  readonly spawnTick: number;
  readonly wordTick: number; // 임계값은 폐지, 카운터는 유지(분노 게이지 입력 · 계측)
  readonly movedTicks: number;
  readonly idealTicks: number;
  readonly segMoved: readonly number[];
  readonly segIdeal: readonly number[];
  readonly segBoundary: number;
  readonly wrongEats: number;
  readonly decoyEats: number;
  readonly outOfOrderEats: number;
  readonly wrongJamoSim: readonly number[];
  readonly hitCells: readonly CellIndex[];
  readonly firstTurnTick: number; // 미발생이면 -1
  readonly relocations: number;
  readonly reviveInWord: boolean; // 이 문제 도중 부활했다 — 우회율 분석에서 걷어내는 유일한 표식
  readonly reviveRelocations: number; // 부활 재배치 — relocateMaxPerWord 예산을 잠식하지 않는다
  readonly perfect: boolean;
  readonly len0: number; // 스폰 시 논리 길이
  readonly autoGrown: number; // 이 문제 동안 자동 성장 칸
  readonly wrongGrown: number; // 이 문제 동안 오답 성장 칸(클램프 후 실적용)
  readonly bestRemaining: number;
  readonly noProgressTicks: number;
  readonly sealedTicks: number;
}

export interface PenaltyState {
  readonly status: PenaltyStatus;
  readonly ticksLeft: number;
  readonly deferTicksLeft: number;
}

export interface RngStreams {
  readonly quiz: number;
  readonly layout: number;
  readonly decoy: number;
  readonly respawn: number;
  readonly revive: number; // 판 시드 파생 · 판당 1회만 소비
}

export interface WordRecord {
  readonly index: number;
  readonly questionId: string;
  readonly answerLen: number;
  readonly outcome: WordOutcome;
  readonly showTick: number;
  readonly spawnTick: number;
  readonly endTick: number;
  readonly movedTicks: number;
  readonly idealTicks: number;
  readonly segMoved: readonly number[];
  readonly segIdeal: readonly number[];
  readonly wrongEats: number;
  readonly decoyEats: number;
  readonly outOfOrderEats: number;
  readonly firstTurnTick: number;
  readonly relocations: number;
  readonly reviveInWord: boolean;
  readonly wordTick: number; // 이 문제에서 흐른 PLAY 틱 — 분노 단계의 입력
  readonly perfect: boolean;
  readonly len0: number; // 스폰 시 논리 길이
  readonly len1: number; // 닫힐 때 논리 길이
  readonly autoGrown: number;
  readonly wrongGrown: number;
  readonly nominalMs: number;
  readonly wrongJamoSim: readonly number[];
}

export type EngineEvent =
  | {
      readonly kind: 'word_spawn';
      readonly index: number;
      readonly id: string;
      readonly idealTicks: number;
    }
  | { readonly kind: 'eat_correct'; readonly slot: number; readonly cell: CellIndex }
  | {
      readonly kind: 'eat_wrong';
      readonly cell: CellIndex;
      readonly ch: string;
      readonly slot: number; // -1 = 오답 타일 · >=0 = 순서 위반
      readonly jamoSim: number;
      readonly atFloor: boolean;
      readonly firstHit: boolean;
    }
  | { readonly kind: 'word_end'; readonly record: WordRecord }
  | { readonly kind: 'grow'; readonly length: number } // 자동 성장 발동 (논리 길이)
  | { readonly kind: 'revive_offer'; readonly questionId: string; readonly cause: DeathCause }
  | {
      readonly kind: 'revive_judge';
      readonly result: Exclude<ReviveResult, 'none'>;
      readonly picked: number;
      readonly answerTicks: number;
    }
  | { readonly kind: 'revive_done'; readonly used: boolean }
  | {
      readonly kind: 'relocate';
      readonly reason: 'no-progress' | 'sealed' | 'revive';
      readonly count: number;
    }
  | { readonly kind: 'penalty'; readonly status: PenaltyStatus; readonly ticksLeft: number }
  | { readonly kind: 'spawn_degraded'; readonly wantTiles: number; readonly gotTiles: number }
  | { readonly kind: 'spawn_relaxed'; readonly slot: number; readonly level: 0 | 1 | 2 | 3 }
  | { readonly kind: 'game_over'; readonly reason: EndReason };

export interface EngineState {
  readonly v: 1;
  readonly engineVersion: number;
  readonly gridVersion: number;
  readonly seed: string; // 정규화된 판 시드 8자
  readonly sessionSeed: string;
  readonly runIndex: number;
  readonly packVersion: string;
  readonly packHash: string;
  readonly engineRulesHash: string;
  readonly tick: number;
  readonly nominalMs: number; // = tick × nominalTickMs
  readonly phase: Phase;
  readonly endReason: EndReason;
  readonly readyTicksLeft: number;
  readonly clearTicksLeft: number;
  readonly snake: SnakeState;
  readonly tiles: readonly Tile[];
  readonly word: WordState;
  readonly words: readonly string[];
  readonly penalty: PenaltyState;
  readonly revive: ReviveState;
  readonly reviveTicks: number; // 부활 페이즈에서 보낸 틱 수 (판정 도구가 tk 에서 뺀다)
  readonly rng: RngStreams;
  readonly moveTicks: number; // 머리가 실제로 움직인 틱 수
  readonly growTicks: number; // 다음 자동 성장까지의 이동 틱 카운터
  readonly growPauseLeft: number; // 단어 해결 후 성장 정지 잔여 이동 틱
  readonly peakLength: number; // 최대 논리 길이 — "얼마나 몰렸나"
  readonly lastSolveTick: number; // 마지막 정답 완성 틱 (-1 = 없음)
  readonly ledger: LenLedger;
  readonly spawnDegraded: number;
  readonly spawnRelaxed: number;
  readonly records: readonly WordRecord[];
  readonly events: readonly EngineEvent[]; // 이번 틱에 발생한 것만
}

export interface EngineCtx {
  readonly rules: EngineRules;
  readonly pack: ContentPack;
  /** 팩 로케일의 알파벳 모듈 — 사다리·유사도·힌트(RULES §1.17) */
  readonly alphabet: Alphabet;
  readonly ordered: readonly Question[];
  readonly engineRulesHash: string;
  readonly packHash: string;
}

export interface InputEntry {
  readonly t: number;
  readonly d: Dir;
}
export type InputLog = readonly InputEntry[];

export interface RunArgs {
  readonly seed: string;
  readonly pack: ContentPack;
  readonly rules: EngineRules;
  readonly inputLog: InputLog;
  readonly choiceLog?: ChoiceLog; // 미지정 = [] = 부활 전부 시간 초과
  readonly session?: { readonly sessionSeed: string; readonly runIndex: number };
  readonly maxTicks?: number;
  readonly onTick?: (s: EngineState) => void;
}

export interface RunResult {
  readonly engineVersion: number;
  readonly gridVersion: number;
  readonly seed: string;
  readonly sessionSeed: string;
  readonly runIndex: number;
  readonly packVersion: string;
  readonly packHash: string;
  readonly engineRulesHash: string;
  readonly ticks: number;
  readonly nominalMs: number;
  readonly endReason: EndReason;
  readonly sealed: boolean; // solved === runWordCount — endReason 을 보지 않는다
  readonly solved: number;
  readonly lastSolveTick: number; // -1 = 없음
  readonly sealTicks: number; // 봉인 성공 시 마지막 정답 틱(WORD_CLEAR 제외), 아니면 -1
  readonly peakLength: number;
  readonly moveTicks: number;
  readonly autoGrown: number;
  readonly wrongEats: number;
  readonly spawnDegraded: number;
  readonly spawnRelaxed: number;
  readonly ledger: LenLedger;
  readonly reviveOffered: number;
  readonly reviveUsed: number;
  readonly reviveResult: ReviveResult;
  readonly reviveQuestionId: string;
  readonly reviveAnswerTicks: number; // 미제시 -1
  readonly reviveTicks: number;
  readonly records: readonly WordRecord[];
  readonly checksum: string;
}

export class EngineContractError extends Error {
  constructor(
    readonly field: string,
    readonly expected: string,
    readonly actual: string,
  ) {
    super(`engine: ${field} 불일치 (state=${expected} ctx=${actual})`);
    this.name = 'EngineContractError';
  }
}
